import { IActionDefinitionDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import { canManageActions, deleteActionFromRow, hasActionRowMenu } from "../datasource-row-actions";
import { getDatasourceDisplayName } from "../datasources-landing-model";
import { EditorActionKind, getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { buildActionFolders, buildActionSwitcherItems } from "./editor-switcher-items";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * The identity chip on the SQL/Select action editor's bar: which action is open, and the other
 * actions of the same
 * datasource, in that datasource's action folders. Name uniqueness is per datasource, and the
 * editor is mounted against one, so that is the list it reaches.
 *
 * No "+": a new action is either a SQL one or a Select one, and that choice is made where the
 * action is created, not here. Its "…" offers what the datasources list offers an action row —
 * Delete, behind the same confirmation — kept beside the popover so a pick that closes the
 * popover does not take the confirmation with it.
 */

/** The two editors this chip is drawn in; CONVERT turns the one into the other. */
export type ActionSwitcherKind = Extract<EditorActionKind, "sql" | "select">;

export interface IActionSwitcherChipProps {
  sd: IScreenDesignContextState;
  setNextCallbackFunction: (callback?: () => void) => void;
  datasource: IDatasourceDto | undefined;
  currentActionId: string | undefined;
  /** Which editor is open — it names the glyph's tooltip: "SQL Action · OrdersDb". */
  actionKind: ActionSwitcherKind;
  /** The open action's name — the editor's own committed copy, which a rename changes. */
  name: string;
  /**
   * On the name inside the chip. Each editor gives its own — `sqlEditorTitle` on the SQL one,
   * `selectActionNameLabel` on the Select one — because the suites tell the two apart by it:
   * CONVERT turns one into the other in place.
   */
  labelTestId: string;
  /** The editor's guarded close — it leads to the datasources list. */
  onManageAll: () => void;
}

export const ActionSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  datasource,
  currentActionId,
  actionKind,
  name,
  labelTestId,
  onManageAll
}: IActionSwitcherChipProps): React.ReactElement => {
  const glyph: IEditorGlyph = getEditorGlyph({
    kind: "action",
    actionKind,
    datasourceName: datasource ? getDatasourceDisplayName(datasource) : undefined
  });
  const { deleteOption, deleteModal }: ISwitcherDelete<IActionDefinitionDto> = useSwitcherDelete(
    "action",
    (action: IActionDefinitionDto): void => void deleteActionFromRow(sd, action)
  );

  // The datasources list's own rule for an action row's Delete.
  const canDeleteAction: boolean = canManageActions(sd) && !!datasource && hasActionRowMenu(datasource);

  const items: KuikaEntitySwitcherItem[] = buildActionSwitcherItems(sd.actionDefinitions ?? [], datasource?.id);
  const current: KuikaEntitySwitcherItem | undefined = items.find(
    (item: KuikaEntitySwitcherItem): boolean => item.id === currentActionId
  );
  const find = (id: string): IActionDefinitionDto | undefined =>
    (sd.actionDefinitions ?? []).find((candidate: IActionDefinitionDto): boolean => candidate.id === id);

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={name}
        triggerLabelTestId={labelTestId}
        items={items}
        current={current ? { ...current, name } : undefined}
        entityLabel="Action"
        iconName="database"
        iconType="datasource-icons"
        triggerTestId="actionSwitcherTrigger"
        folders={buildActionFolders(sd.project?.actionFolders, datasource?.id)}
        onManageAll={onManageAll}
        getRowActions={(item: KuikaEntitySwitcherItem): KuikaDropdownMenuItemType[] => {
          const action: IActionDefinitionDto | undefined = find(item.id);
          if (!action) return [];
          return [deleteOption(action, !canDeleteAction)];
        }}
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const action: IActionDefinitionDto | undefined = find(item.id);
          if (!action || !datasource) return;
          setNextCallbackFunction((): void => sd.setEditingActionsDatasource(datasource, action, false, false));
        }}
      />
      {deleteModal}
    </>
  );
};
